@file:Suppress("UNUSED_PARAMETER", "unused", "RedundantSuspendModifier")
@file:NoLiveLiterals

package com.example.borshi.web.firebase

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.model.Product
import com.example.borshi.web.model.ProductInfo
import com.example.borshi.web.model.ProductTags
import com.example.borshi.web.model.Category
import com.example.borshi.web.data.OrderItemType
import com.example.borshi.web.data.WebOrder
import com.example.borshi.web.data.WebOrderItem
import com.example.borshi.web.model.NutritionalTargets
import com.example.borshi.web.model.NutritionistBasePlan
import com.example.borshi.web.model.NutritionistProfile
import com.example.borshi.web.model.PlanCategory
import com.example.borshi.web.model.PricingTemplate
import com.example.borshi.web.model.ProteinPlan
import com.example.borshi.web.model.WebSubscription
import com.example.borshi.web.cart.OrderRequest
import kotlinx.browser.document
import kotlinx.coroutines.await
import kotlin.js.Date
import kotlin.js.Promise

@JsModule("firebase/app")
@JsNonModule
external object FirebaseApp {
    fun initializeApp(options: dynamic): dynamic
    fun getApps(): Array<dynamic>
}

@JsModule("firebase/auth")
@JsNonModule
external object FirebaseAuth {
    fun getAuth(app: dynamic = definedExternally): dynamic
    fun signInWithEmailAndPassword(auth: dynamic, email: String, password: String): Promise<dynamic>
    fun createUserWithEmailAndPassword(auth: dynamic, email: String, password: String): Promise<dynamic>
    fun signOut(auth: dynamic): Promise<Unit>
    fun onAuthStateChanged(auth: dynamic, observer: (dynamic) -> Unit): () -> Unit
    fun sendPasswordResetEmail(auth: dynamic, email: String): Promise<Unit>
    fun signInWithPhoneNumber(auth: dynamic, phoneNumber: String, appVerifier: dynamic): Promise<dynamic>
    fun signInWithPopup(auth: dynamic, provider: dynamic): Promise<dynamic>
    fun signInAnonymously(auth: dynamic): Promise<dynamic>
}

// RecaptchaVerifier as a named export from firebase/auth
@JsModule("firebase/auth")
@JsNonModule
@JsName("RecaptchaVerifier")
external class RecaptchaVerifier(
    auth: dynamic,
    container: String,
    parameters: dynamic = definedExternally
) {
    fun render(): Promise<Number>
    fun verify(): Promise<String>
    fun clear()
}

@JsModule("firebase/firestore")
@JsNonModule
external object FirebaseFirestore {
    fun getFirestore(app: dynamic = definedExternally): dynamic
    fun collection(db: dynamic, path: String): dynamic
    fun doc(db: dynamic, path: String, id: String? = definedExternally): dynamic
    fun getDoc(doc: dynamic): Promise<dynamic>
    fun addDoc(collection: dynamic, data: dynamic): Promise<dynamic>
    fun setDoc(doc: dynamic, data: dynamic): Promise<Unit>
    fun updateDoc(doc: dynamic, data: dynamic): Promise<Unit>
    fun deleteDoc(doc: dynamic): Promise<Unit>
    fun getDocs(query: dynamic): Promise<dynamic>
    fun query(collection: dynamic, vararg constraints: dynamic): dynamic
    fun where(field: String, opStr: String, value: dynamic): dynamic
    fun orderBy(field: String, direction: String? = definedExternally): dynamic
    fun limit(limit: Int): dynamic
    fun startAfter(vararg fieldValues: dynamic): dynamic
    fun onSnapshot(docOrQuery: dynamic, observer: (dynamic) -> Unit): () -> Unit
    fun exists(snapshot: dynamic): Boolean
    fun data(snapshot: dynamic): dynamic
    fun id(doc: dynamic): String
    fun docs(querySnapshot: dynamic): Array<dynamic>
}

@JsModule("firebase/functions")
@JsNonModule
external object FirebaseFunctions {
    fun getFunctions(app: dynamic = definedExternally, region: String? = definedExternally): dynamic
    fun httpsCallable(functions: dynamic, name: String, options: dynamic = definedExternally): (data: dynamic) -> Promise<dynamic>
}

@JsModule("firebase/storage")
@JsNonModule
external object FirebaseStorage {
    fun getStorage(app: dynamic = definedExternally): dynamic
    fun ref(storage: dynamic, path: String): dynamic
    fun listAll(ref: dynamic): Promise<dynamic>
    fun getDownloadURL(ref: dynamic): Promise<String>
}

object FirebaseService {
    private var app: dynamic = null
    private var db: dynamic = null
    private var auth: dynamic = null
    private var functions: dynamic = null
    private var storage: dynamic = null
    private var confirmationResult: dynamic = null
    private var recaptchaVerifier: dynamic = null
    private var initialized = false

    private fun requireUid(): String {
        init()
        val uid = auth?.currentUser?.uid as? String
        if (uid.isNullOrBlank()) throw IllegalStateException("Sign in required")
        return uid
    }

    private fun dynamicToMillis(value: dynamic): Long? {
        return try {
            when {
                value == null -> null
                (js("typeof value.toMillis === 'function'") as Boolean) -> (value.toMillis() as? Number)?.toLong()
                value is Number -> value.toLong()
                else -> null
            }
        } catch (_: Throwable) {
            null
        }
    }

    fun getCurrentUserId(): String? {
        return try {
            init()
            auth?.currentUser?.uid as? String
        } catch (e: Throwable) {
            null
        }
    }

    /** Returns true if user doc has a default address with line1, city, and postalCode (5+ chars). */
    private fun computeHasValidAddress(d: dynamic): Boolean {
        val addrArray = d.addresses as? Array<dynamic> ?: return false
        val defaultOrFirst = addrArray.firstOrNull { addr ->
            (addr.isDefault == true || addr.default == true)
        } ?: addrArray.firstOrNull()
        if (defaultOrFirst == null) return false
        val line1 = (defaultOrFirst.line1 as? String).orEmpty().trim()
        val city = (defaultOrFirst.city as? String).orEmpty().trim()
        val postalCode = (defaultOrFirst.postalCode as? String).orEmpty().replace(Regex("[^0-9]"), "")
        return line1.isNotBlank() && city.isNotBlank() && postalCode.length >= 5
    }

    /**
     * Read user profile from Firestore user document.
     * Falls back to Firebase Auth fields when Firestore data is missing.
     */
    suspend fun getUserProfile(): com.example.borshi.web.auth.UserProfile {
        init()
        val uid = getCurrentUserId() ?: return com.example.borshi.web.auth.UserProfile()
        val authUser = auth?.currentUser

        try {
            val userRef = FirebaseFirestore.doc(db, "users", uid)
            val snap = FirebaseFirestore.getDoc(userRef).await()
            if (snap.exists()) {
                val d = snap.data()
                val hasValidAddress = computeHasValidAddress(d)
                return com.example.borshi.web.auth.UserProfile(
                    userId = uid,
                    name = d.name as? String ?: authUser?.displayName as? String ?: "",
                    phoneNumber = d.phone as? String ?: authUser?.phoneNumber as? String ?: "",
                    email = d.email as? String ?: authUser?.email as? String ?: "",
                    referralCode = d.referralCode as? String,
                    referredBy = d.referredBy as? String,
                    referralCount = (d.referralCount as? Number)?.toInt() ?: 0,
                    hasMadePurchase = d.hasMadePurchase as? Boolean ?: false,
                    referralBonus = (d.referralBonus as? Number)?.toDouble() ?: 0.0,
                    hasValidAddress = hasValidAddress
                )
            }
        } catch (e: Throwable) {
            console.warn("getUserProfile Firestore read failed, using Auth fallback:", e)
        }

        return com.example.borshi.web.auth.UserProfile(
            userId = uid,
            name = authUser?.displayName as? String,
            phoneNumber = authUser?.phoneNumber as? String,
            email = authUser?.email as? String
        )
    }

    fun getFirestore(): dynamic {
        init()
        return db
    }

    fun getFunctions(): dynamic {
        init()
        return functions
    }

    suspend fun createSubscription(subscription: WebSubscription): String {
        val uid = requireUid()
        val col = FirebaseFirestore.collection(db, "subscriptions")
        val data = js("({})")
        data.userId = uid
        data.productId = subscription.productId
        data.productName = subscription.productName
        data.quantity = subscription.quantity
        data.frequency = subscription.frequency.name
        data.startDate = Date(subscription.startDateMillis)
        data.status = "ACTIVE"
        data.preferredDeliveryDay = subscription.preferredDeliveryDay
        data.customDays = subscription.customDays
        data.lastDeliveryDate = null
        data.createdAt = Date.now()
        val docRef = FirebaseFirestore.addDoc(col, data).await()
        return docRef.id as? String ?: ""
    }

    fun onAuthStateChanged(observer: (dynamic) -> Unit): () -> Unit {
        init()
        return FirebaseAuth.onAuthStateChanged(auth, observer)
    }

    fun sendPasswordReset(email: String) {
        init()
        FirebaseAuth.sendPasswordResetEmail(auth, email)
    }

    private suspend fun callFunction(name: String, payload: dynamic): dynamic {
        requireUid()
        val callable = FirebaseFunctions.httpsCallable(functions, name)
        return try {
            val result = callable(payload).await()
            result.data ?: result
        } catch (e: Throwable) {
            val msg = e.message ?: e.toString()
            console.error("Function $name error:", e)
            throw RuntimeException("Function $name failed: $msg")
        }
    }

    /**
     * Send a message to the Octopus AI chat Cloud Function.
     * Matches the payload expected by octopus.functions.ts:
     *   { sessionId, message, history: [{role, content}] }
     * Returns the reply string, falling back to null on any error.
     */
    suspend fun sendOctopusMessage(
        sessionId: String,
        message: String,
        history: List<Pair<String, String>> // role to content
    ): String? {
        return try {
            init()
            val payload = js("({})")
            payload.sessionId = sessionId
            payload.message = message
            val historyArray = js("[]")
            history.forEach { (role, content) ->
                val entry = js("({})")
                entry.role = role
                entry.content = content
                historyArray.push(entry)
            }
            payload.history = historyArray
            val result = callFunction("octopusChat", payload)
            result?.text as? String
        } catch (e: Throwable) {
            console.warn("octopusChat unavailable, using local fallback:", e.message)
            null // caller will use local fallback
        }
    }

    /**
     * Calls the generateReferralCode cloud function to ensure the current user
     * has a referral code assigned. Returns the code string, or null on failure.
     * Safe to call even if the user already has a code — the function is idempotent.
     */
    suspend fun ensureReferralCode(): String? {
        return try {
            init()
            val payload = js("({})")
            val result = callFunction("generateReferralCode", payload)
            result?.code as? String
        } catch (e: Throwable) {
            console.error("ensureReferralCode failed:", e.message)
            null
        }
    }

    suspend fun addMoney(amount: Double) {
        val payload = js("({})")
        payload.amount = amount
        callFunction("addMoney", payload)
    }

    suspend fun createDeliveryOverride(deliveryId: String, action: String, quantity: Int?) {
        val payload = js("({})")
        payload.deliveryId = deliveryId
        payload.action = action
        if (action == "MODIFY" && quantity != null) {
            payload.quantity = quantity
        }
        callFunction("createDeliveryOverride", payload)
    }

    suspend fun addVacation(startDate: String, endDate: String) {
        val uid = requireUid()
        val col = FirebaseFirestore.collection(db, "users/$uid/vacations")
        val data = js("({})")
        data.startDate = Date(startDate).getTime()
        data.endDate = Date(endDate).getTime()
        data.label = "Vacation"
        data.createdAt = Date.now()
        FirebaseFirestore.addDoc(col, data).await()
    }

    suspend fun updateSubscriptionStatus(subscriptionId: String, status: String) {
        init()
        val docRef = FirebaseFirestore.doc(db, "subscriptions", subscriptionId)
        val data = js("({})")
        data.status = status
        FirebaseFirestore.updateDoc(docRef, data).await()
    }

    suspend fun getWalletBalance(userId: String): Double {
        val uid = requireUid() // TODO remove userId param once call sites are updated
        try {
            // NEW STRUCTURE: Check users/{uid}/wallet/balance
            val walletBalanceRef = FirebaseFirestore.doc(db, "users/$uid/wallet/balance")
            val docSnap = FirebaseFirestore.getDoc(walletBalanceRef).await()

            if (docSnap.exists()) {
                val data = docSnap.data()
                return (data.currentBalance as? Number)?.toDouble()
                    ?: (data.availableBalance?.minorUnits as? Number)?.toDouble()?.div(100) // Fallback for Money object structure
                    ?: 0.0
            }

            // Legacy Fallback: Check user document
            val userRef = FirebaseFirestore.doc(db, "users", uid)
            val userSnap = FirebaseFirestore.getDoc(userRef).await()
            if (userSnap.exists()) {
                val data = userSnap.data()
                return (data.walletBalance as? Number)?.toDouble() ?: 0.0
            }
        } catch (e: Throwable) {
            console.error("Error fetching wallet balance:", e)
        }
        return 0.0
    }

    // Real-time listener for wallet balance
    fun watchWalletBalance(onUpdate: (Double) -> Unit): () -> Unit {
        val uid = try { requireUid() } catch (e: Exception) { return {} }

        console.log("Setting up wallet watcher for $uid")
        val walletBalanceRef = FirebaseFirestore.doc(db, "users/$uid/wallet/balance")

        return FirebaseFirestore.onSnapshot(walletBalanceRef) { snapshot ->
            try {
                if (snapshot.exists()) {
                    val data = snapshot.data()
                    val balance = (data.currentBalance as? Number)?.toDouble()
                        ?: (data.availableBalance?.minorUnits as? Number)?.toDouble()?.div(100)
                        ?: 0.0
                    onUpdate(balance)
                } else {
                    // Try legacy location watcher if main one fails/doesn't exist?
                    // Probably better to just return 0.0 or do nothing until migrated
                    onUpdate(0.0)
                }
            } catch (e: Throwable) {
                console.error("Error in wallet watcher:", e)
            }
        }
    }

    suspend fun getWalletTransactions(): List<WalletTransaction> {
        val uid = requireUid() // TODO remove userId param once call sites are updated
        try {
            // NEW STRUCTURE: users/{uid}/wallet/balance/transactions
            val transactionsCol = FirebaseFirestore.collection(db, "users/$uid/wallet/balance/transactions")

            val q = FirebaseFirestore.query(
                transactionsCol,
                FirebaseFirestore.orderBy("timestamp", "desc"), // New field name
                FirebaseFirestore.limit(50)
            )

            val snapshot = FirebaseFirestore.getDocs(q).await()
            val docs = snapshot.docs as Array<dynamic>

            // If new structure is empty, try legacy: users/{uid}/wallet_transactions
            if (docs.isEmpty()) {
                 return getLegacyWalletTransactions(uid)
            }

            return docs.map { doc ->
                val data = doc.data()
                val tsMillis = dynamicToMillis(data.timestamp) ?: dynamicToMillis(data.createdAt) ?: 0L

                WalletTransaction(
                    id = doc.id as? String ?: "",
                    amount = (data.amount as? Number)?.toDouble() ?: 0.0,
                    type = data.type as? String ?: "UNKNOWN",
                    description = data.description as? String ?: "",
                    timestampMillis = tsMillis
                )
            }.sortedByDescending { it.timestampMillis }
        } catch (e: Throwable) {
            console.error("Error fetching transactions:", e)
            return emptyList()
        }
    }

    private suspend fun getLegacyWalletTransactions(uid: String): List<WalletTransaction> {
        try {
            val userRef = FirebaseFirestore.doc(db, "users", uid)
            val transactionsCol = FirebaseFirestore.collection(userRef, "wallet_transactions")
            val q = FirebaseFirestore.query(
                transactionsCol,
                FirebaseFirestore.orderBy("createdAt", "desc"),
                FirebaseFirestore.limit(50)
            )
            val snapshot = FirebaseFirestore.getDocs(q).await()
            val docs = snapshot.docs as Array<dynamic>
            return docs.map { doc ->
                val data = doc.data()
                val tsMillis = dynamicToMillis(data.createdAt) ?: 0L

                WalletTransaction(
                    id = doc.id as? String ?: "",
                    amount = (data.amount as? Number)?.toDouble() ?: 0.0,
                    type = data.type as? String ?: "UNKNOWN",
                    description = data.description as? String ?: "",
                    timestampMillis = tsMillis
                )
            }.sortedByDescending { it.timestampMillis }
        } catch (e: Throwable) {
            return emptyList()
        }
    }

    suspend fun getDailyDeliveries(userId: String? = null): List<DailyDelivery> {
        val uid = userId?.ifBlank { null } ?: requireUid()
        try {
            val col = FirebaseFirestore.collection(db, "daily_deliveries")
            val q = FirebaseFirestore.query(
                col,
                FirebaseFirestore.where("userId", "==", uid),
                FirebaseFirestore.where("status", "==", "SCHEDULED"),
                FirebaseFirestore.orderBy("date"),
                FirebaseFirestore.limit(10)
            )
            val snapshot = FirebaseFirestore.getDocs(q).await()
            val docs = snapshot.docs as Array<dynamic>
            return docs.map { doc ->
                val data = doc.data()
                DailyDelivery(
                    id = doc.id as? String ?: "",
                    productName = data.productName as? String ?: "Unknown Item",
                    quantity = (data.quantity as? Number)?.toInt() ?: 1,
                    date = data.date as? String ?: "Today",
                    status = data.status as? String ?: "SCHEDULED"
                )
            }
        } catch (e: Throwable) {
            console.error("Error fetching deliveries:", e)
            return emptyList()
        }
    }

    suspend fun getVacations(): List<Vacation> {
        val uid = requireUid()
        try {
            val col = FirebaseFirestore.collection(db, "users/$uid/vacations")
            val snapshot = FirebaseFirestore.getDocs(col).await()
            val docs = snapshot.docs as Array<dynamic>
            return docs.map { doc ->
                val data = doc.data()
                Vacation(
                    id = doc.id as? String ?: "",
                    startDate = (dynamicToMillis(data.startDate)?.let { Date(it) } ?: Date()),
                    endDate = (dynamicToMillis(data.endDate)?.let { Date(it) } ?: Date()),
                    label = data.label as? String ?: "Vacation"
                )
            }.sortedBy { it.startDate.getTime() }
        } catch (e: Throwable) {
            console.error("Error fetching vacations:", e)
            return emptyList()
        }
    }

    /**
     * Fetches all orders for the current user from Firestore.
     * Handles BOTH order structures:
     *  - New (Cashfree): cartItems[], customerDetails{}, orderStatus, paymentStatus, totalAmount, cashfreeOrderId
     *  - Old (legacy):   items[], customerName, customerPhone, status, paymentMethod, payTodayAmount, totalAmount
     */
    suspend fun getOrders(): List<WebOrder> {
        val uid = requireUid()
        val result = mutableListOf<WebOrder>()
        try {
            val col = FirebaseFirestore.collection(db, "orders")
            val q = FirebaseFirestore.query(col, FirebaseFirestore.where("userId", "==", uid))
            val snap = FirebaseFirestore.getDocs(q).await()
            val docs = snap.docs.unsafeCast<Array<dynamic>>()
            docs.forEach { doc ->
                val d = doc.data()
                val docId = doc.id as? String ?: ""

                // ── Timestamps ────────────────────────────────────────────
                // IMPORTANT: toMillis() returns a JS number (Double), NOT a Kotlin Long.
                // unsafeCast<Long>() does NOT convert — it just lies to the type system.
                // Always go through (as Number).toLong() to get a real Kotlin Long.
                val createdAtMs: Long = when {
                    d.createdAt != null && js("typeof d.createdAt.toMillis === 'function'").unsafeCast<Boolean>() ->
                        (d.createdAt.toMillis() as Number).toLong()
                    d.createdAt != null && js("typeof d.createdAt === 'number'").unsafeCast<Boolean>() ->
                        (d.createdAt as Number).toLong()
                    else -> 0L
                }
                val updatedAtMs: Long = when {
                    d.updatedAt != null && js("typeof d.updatedAt.toMillis === 'function'").unsafeCast<Boolean>() ->
                        (d.updatedAt.toMillis() as Number).toLong()
                    d.updatedAt != null && js("typeof d.updatedAt === 'number'").unsafeCast<Boolean>() ->
                        (d.updatedAt as Number).toLong()
                    else -> 0L
                }
                val paidAtMs: Long = when {
                    d.paidAt != null && js("typeof d.paidAt.toMillis === 'function'").unsafeCast<Boolean>() ->
                        (d.paidAt.toMillis() as Number).toLong()
                    d.paidAt != null && js("typeof d.paidAt === 'number'").unsafeCast<Boolean>() ->
                        (d.paidAt as Number).toLong()
                    else -> 0L
                }

                // ── NEW structure (Cashfree) ───────────────────────────────
                val isNewStructure = d.cashfreeOrderId != null || d.cartItems != null

                val status: String
                val paymentStatus: String
                val paymentMethod: String
                val customerName: String
                val customerPhone: String
                val deliveryAddress: String
                val totalAmount: Double
                val payTodayAmount: Double
                val cashfreeOrderId: String
                val items: List<WebOrderItem>

                if (isNewStructure) {
                    status        = d.orderStatus  as? String ?: "CREATED"
                    paymentStatus = d.paymentStatus as? String ?: "PENDING"
                    paymentMethod = "Cashfree"
                    cashfreeOrderId = d.cashfreeOrderId as? String ?: d.orderId as? String ?: docId
                    totalAmount   = (d.totalAmount  as? Number)?.toDouble() ?: (d.cashfreeResponse?.order_amount as? Number)?.toDouble() ?: 0.0
                    payTodayAmount = totalAmount
                    // customerDetails sub-map
                    val cd = d.customerDetails
                    customerName  = cd?.name  as? String ?: cd?.customer_name  as? String ?: ""
                    customerPhone = cd?.phone as? String ?: cd?.customer_phone as? String ?: ""
                    deliveryAddress = d.deliveryAddress as? String ?: ""
                    // cartItems array
                    val cartArr = d.cartItems.unsafeCast<Array<dynamic>?>() ?: emptyArray()
                    items = cartArr.map { ci ->
                        val pid = ci.productId as? String ?: ""
                        // New orders don't store imageUrl in cartItems — derive from productId
                        val imgUrl = (ci.imageUrl as? String)?.takeIf { it.isNotBlank() }
                            ?: if (pid.isNotBlank()) getProductImageUrl(pid) else null
                        WebOrderItem(
                            productId = pid,
                            name      = ci.productName as? String ?: ci.name as? String ?: "",
                            quantity  = (ci.quantity as? Number)?.toInt() ?: 1,
                            price     = (ci.price as? Number)?.toDouble() ?: 0.0,
                            type      = OrderItemType.ONE_TIME,
                            imageUrl  = imgUrl
                        )
                    }
                } else {
                    // ── OLD structure (legacy) ──────────────────────────────
                    status          = d.status        as? String ?: "pending"
                    paymentStatus   = if (status == "delivered") "PAID" else d.paymentStatus as? String ?: status.uppercase()
                    paymentMethod   = d.paymentMethod as? String ?: "Cash on Delivery"
                    cashfreeOrderId = ""
                    totalAmount     = (d.totalAmount   as? Number)?.toDouble() ?: 0.0
                    payTodayAmount  = (d.payTodayAmount as? Number)?.toDouble() ?: totalAmount
                    customerName    = d.customerName  as? String ?: ""
                    customerPhone   = d.customerPhone as? String ?: ""
                    deliveryAddress = d.deliveryAddress as? String ?: ""
                    val itemArr = d.items.unsafeCast<Array<dynamic>?>() ?: emptyArray()
                    items = itemArr.map { li ->
                        val pid = li.productId as? String ?: ""
                        val imgUrl = (li.imageUrl as? String)?.takeIf { it.isNotBlank() }
                            ?: if (pid.isNotBlank()) getProductImageUrl(pid) else null
                        WebOrderItem(
                            productId       = pid,
                            name            = li.name            as? String ?: "",
                            quantity        = (li.quantity as? Number)?.toInt() ?: 1,
                            price           = (li.price    as? Number)?.toDouble() ?: 0.0,
                            type            = if ((li.type as? String) == "SUBSCRIPTION") OrderItemType.SUBSCRIPTION else OrderItemType.ONE_TIME,
                            packSize        = li.packSize        as? String,
                            weight          = li.weight          as? String,
                            scheduleSummary = li.scheduleSummary as? String,
                            imageUrl        = imgUrl
                        )
                    }
                }

                result.add(
                    WebOrder(
                        id              = docId,
                        userId          = uid,
                        customerName    = customerName,
                        customerPhone   = customerPhone,
                        deliveryAddress = deliveryAddress,
                        status          = status,
                        paymentStatus   = paymentStatus,
                        paymentMethod   = paymentMethod,
                        cashfreeOrderId = cashfreeOrderId,
                        totalAmount     = totalAmount,
                        payTodayAmount  = payTodayAmount,
                        createdAt       = createdAtMs,
                        updatedAt       = updatedAtMs,
                        paidAt          = paidAtMs,
                        items           = items
                    )
                )
            }
            // Most recent first — use Double comparison (JS-safe, avoids Long.compareTo crash)
            result.sortWith(Comparator { a, b ->
                val diff = b.createdAt.toDouble() - a.createdAt.toDouble()
                when {
                    diff > 0 -> 1
                    diff < 0 -> -1
                    else -> 0
                }
            })
        } catch (e: Throwable) {
            console.error("getOrders failed:", e)
            throw e
        }
        return result
    }

    suspend fun deleteVacation(vacationId: String) {
        val uid = requireUid()
        val doc = FirebaseFirestore.doc(db, "users/$uid/vacations", vacationId)
        FirebaseFirestore.deleteDoc(doc).await()
    }

    suspend fun getAddresses(): List<SavedAddress> {
        val uid = requireUid()
        console.log("Fetching addresses for user:", uid)
        val addresses = mutableListOf<SavedAddress>()
        try {
            val userRef = FirebaseFirestore.doc(db, "users", uid)
            val userSnap = FirebaseFirestore.getDoc(userRef).await()
            console.log("User snapshot exists:", userSnap.exists())
            if (userSnap.exists()) {
                val userData = userSnap.data()
                console.log("User data addresses raw:", userData.addresses)
                console.log("Type of addresses:", js("typeof userData.addresses"))

                val addrArray = userData.addresses as? Array<dynamic>
                console.log("Address array parsed:", addrArray, "Length:", addrArray?.size ?: 0)

                // Normalise: convert any string entries (legacy plain-text addresses) into
                // proper address objects so the rest of the code can safely access .id, .name, etc.
                addrArray?.forEachIndexed { idx, entry ->
                    if (js("typeof entry === 'string'").unsafeCast<Boolean>()) {
                        val obj = js("({})")
                        obj.id = ""
                        obj.line1 = entry as String
                        obj.name = "Home"
                        addrArray[idx] = obj
                        console.log("Converted legacy string address at $idx to object:", obj)
                    }
                }

                // Repair: assign stable IDs to any address missing one
                var needsRepair = false
                addrArray?.forEachIndexed { idx, data ->
                    val existingId = data.id as? String
                    if (existingId.isNullOrBlank()) {
                        data.id = "${(Date.now().toLong() + idx)}"
                        needsRepair = true
                        console.log("Assigned new id to address at index $idx: ${data.id}")
                    }
                }
                if (needsRepair && addrArray != null) {
                    val repairData = js("({})")
                    repairData.addresses = addrArray
                    FirebaseFirestore.updateDoc(userRef, repairData).await()
                    console.log("Repaired missing address IDs and persisted to Firestore")
                }

                addrArray?.forEach { data ->
                     console.log("Processing address:", data)
                     val parts = listOfNotNull(
                        data.doorNumber as? String,
                        (data.floor as? String)?.let { "Floor $it" },
                        data.block as? String,
                        data.wing as? String,
                        data.apartment as? String
                    ).filter { it.isNotBlank() }

                    val constructedLine1 = parts.joinToString(", ")
                    val finalLine1 = if (constructedLine1.isNotBlank()) constructedLine1 else (data.line1 as? String ?: "")

                    val parsedAddress = SavedAddress(
                        id = data.id as? String ?: "",
                        name = data.name as? String ?: "Home",
                        phone = data.phone as? String ?: "",
                        email = data.email as? String ?: "",
                        line1 = finalLine1,
                        line2 = data.line2 as? String ?: data.landmark as? String ?: "",
                        city = data.city as? String ?: "",
                        state = data.state as? String ?: "",
                        postalCode = data.postalCode as? String ?: "",
                        isDefault = data.default as? Boolean ?: false
                    )
                    console.log("Parsed address:", parsedAddress)
                    addresses.add(parsedAddress)
                }
            } else {
                console.warn("User document does not exist for uid:", uid)
                console.log("Creating user document...")
                // Create user document with empty addresses array
                val userData = js("({})")
                userData.addresses = js("[]")
                userData.createdAt = Date.now()
                FirebaseFirestore.setDoc(userRef, userData).await()
                console.log("User document created")
            }
        } catch (e: Throwable) {
            console.error("Error fetching addresses:", e)
            console.error("Error stack:", e.asDynamic().stack)
        }
        console.log("Returning", addresses.size, "addresses")
        return addresses
    }

    // ── Appointments ──────────────────────────────────────────────

    suspend fun saveAppointment(
        planId: String,
        planName: String,
        nutritionistId: String,
        nutritionistName: String,
        slotId: String,
        slotLabel: String,
        slotEpoch: Long,
        favouriteItemIds: List<String>
    ): String {
        val uid = requireUid()
        val colRef = FirebaseFirestore.collection(db, "users/$uid/appointments")
        val data = js("({})")
        data.planId = planId
        data.planName = planName
        data.nutritionistId = nutritionistId
        data.nutritionistName = nutritionistName
        data.slotId = slotId
        data.slotLabel = slotLabel
        data.slotEpoch = slotEpoch
        data.favouriteItemIds = favouriteItemIds.toTypedArray()
        data.status = "upcoming"
        data.createdAt = Date.now()
        val docRef = FirebaseFirestore.addDoc(colRef, data).await()
        return docRef.id as String
    }

    data class Appointment(
        val id: String,
        val planId: String,
        val planName: String,
        val nutritionistName: String,
        val slotLabel: String,
        val slotEpoch: Long,
        val status: String,
        val createdAt: Long
    )

    suspend fun getAppointments(): List<Appointment> {
        val uid = requireUid()
        val colRef = FirebaseFirestore.collection(db, "users/$uid/appointments")
        val snap = FirebaseFirestore.getDocs(colRef).await()
        val appointments = mutableListOf<Appointment>()
        val docs = snap.docs as Array<dynamic>
        docs.forEach { doc ->
            val d = doc.data()
            appointments.add(Appointment(
                id = doc.id as String,
                planId = d.planId as? String ?: "",
                planName = d.planName as? String ?: "",
                nutritionistName = d.nutritionistName as? String ?: "",
                slotLabel = d.slotLabel as? String ?: "",
                slotEpoch = (d.slotEpoch as? Number)?.toLong() ?: 0L,
                status = d.status as? String ?: "upcoming",
                createdAt = (d.createdAt as? Number)?.toLong() ?: 0L
            ))
        }
        return appointments.sortedByDescending { it.slotEpoch }
    }

    suspend fun saveAddress(address: SavedAddress): String {
        val uid = requireUid()
        val userRef = FirebaseFirestore.doc(db, "users", uid)
        try {
            val userSnap = FirebaseFirestore.getDoc(userRef).await()

            // Create user document if it doesn't exist
            if (!userSnap.exists()) {
                console.log("User document doesn't exist, creating...")
                val newUserData = js("({})")
                newUserData.addresses = js("[]")
                newUserData.createdAt = Date.now()
                FirebaseFirestore.setDoc(userRef, newUserData).await()
            }

            // Now save the address
            val currentUserSnap = FirebaseFirestore.getDoc(userRef).await()
            val userData = currentUserSnap.data()
            val currentAddresses = (userData.addresses as? Array<dynamic>)?.toMutableList() ?: mutableListOf()

            val newId = if (address.id.isBlank()) Date.now().toString() else address.id

            // Build the address object, merging over existing Firestore entry to preserve
            // native fields (doorNumber, floor, block, wing, apartment, landmark, etc.)
            val existingIndex = currentAddresses.indexOfFirst { addr ->
                val storedId = addr.id?.toString() ?: ""
                // Primary match: id matches
                storedId == newId ||
                // Fallback for legacy addresses saved without an id (stored as "" or "undefined")
                (address.id.isNotBlank() && (storedId == "undefined" || storedId.isBlank()) &&
                    addr.name?.toString() == address.name && addr.line1?.toString() == address.line1)
            }
            val addressObj = if (existingIndex >= 0) currentAddresses[existingIndex] else js("({})")
            addressObj.id = newId
            addressObj.name = address.name
            addressObj.phone = address.phone
            addressObj.email = address.email
            addressObj.line1 = address.line1
            addressObj.line2 = address.line2
            addressObj.city = address.city
            addressObj.state = address.state
            addressObj.postalCode = address.postalCode
            addressObj.default = address.isDefault

            if (existingIndex >= 0) {
                currentAddresses[existingIndex] = addressObj
                console.log("Updated existing address at index $existingIndex, id=$newId")
            } else {
                currentAddresses.add(addressObj)
                console.log("Added new address, id=$newId")
            }

            val updateData = js("({})")
            updateData.addresses = currentAddresses.toTypedArray()
            FirebaseFirestore.updateDoc(userRef, updateData).await()
            return newId
        } catch (e: Throwable) {
            console.error("Error saving address:", e)
            throw e
        }
    }

    suspend fun deleteAddress(addressId: String) {
        val uid = requireUid()
        val userRef = FirebaseFirestore.doc(db, "users", uid)
        try {
            val userSnap = FirebaseFirestore.getDoc(userRef).await()
            if (userSnap.exists()) {
                val userData = userSnap.data()
                val currentAddresses = (userData.addresses as? Array<dynamic>)?.toMutableList() ?: mutableListOf()
                console.log("Deleting address with id=$addressId, total before=${currentAddresses.size}")
                val filtered = currentAddresses.filter { addr ->
                    val storedId = addr.id?.toString() ?: ""
                    // Keep addresses that do NOT match the target id
                    // Also treat "undefined" and "" as matching a blank addressId (legacy data)
                    if (addressId.isBlank()) {
                        storedId != "" && storedId != "undefined"
                    } else {
                        storedId != addressId
                    }
                }.toTypedArray()
                console.log("Total after delete=${filtered.size}")

                val updateData = js("({})")
                updateData.addresses = filtered
                FirebaseFirestore.updateDoc(userRef, updateData).await()
            }
        } catch (e: Throwable) {
            console.error("Error deleting address:", e)
            throw e
        }
    }

    suspend fun setDefaultAddress(addressId: String) {
        val uid = requireUid()
        val userRef = FirebaseFirestore.doc(db, "users", uid)
        try {
            val userSnap = FirebaseFirestore.getDoc(userRef).await()
            if (userSnap.exists()) {
                val userData = userSnap.data()
                val currentAddresses = (userData.addresses as? Array<dynamic>)?.toMutableList() ?: mutableListOf()
                // Set default = true only for the chosen address, false for all others
                val updated = currentAddresses.map { addr ->
                    val copy = js("({})")
                    val storedId = addr.id?.toString() ?: ""
                    copy.id = addr.id
                    copy.name = addr.name
                    copy.line1 = addr.line1
                    copy.line2 = addr.line2
                    copy.city = addr.city
                    copy.state = addr.state
                    copy.postalCode = addr.postalCode
                    copy.phone = addr.phone
                    copy.doorNumber = addr.doorNumber
                    copy.floor = addr.floor
                    copy.block = addr.block
                    copy.wing = addr.wing
                    copy.apartment = addr.apartment
                    copy.landmark = addr.landmark
                    copy.default = (storedId == addressId && storedId.isNotBlank() && storedId != "undefined")
                    copy
                }.toTypedArray()

                val updateData = js("({})")
                updateData.addresses = updated
                FirebaseFirestore.updateDoc(userRef, updateData).await()
                console.log("Default address set to:", addressId)
            }
        } catch (e: Throwable) {
            console.error("Error setting default address:", e)
            throw e
        }
    }

    private fun getCategoryImageUrl(rawName: String, displayName: String): String {
        val bucket = "borshi-6fafe.firebasestorage.app"

        // Direct mapping: Image name matches category name/ID
        // e.g. "cat_meat_chicken" -> "cat_meat_chicken.avif"
        // We trim/lowercase for safety but assume user has renamed images to match
        val safeName = rawName.trim().ifEmpty { displayName }.lowercase()
            .replace(Regex("[^a-z0-9_]"), "_") // Maintain underscores, remove other special chars

        val filename = "${safeName}.avif"
        val path = "category/$filename"
        val encodedPath = js("encodeURIComponent")(path) as String
        return "https://firebasestorage.googleapis.com/v0/b/$bucket/o/$encodedPath?alt=media"
    }

    /** Placeholder for broken/missing images — uses Borshi icon from Firebase Storage */
    fun getPlaceholderImageUrl(): String =
        "https://firebasestorage.googleapis.com/v0/b/borshi-6fafe.firebasestorage.app/o/Icons%2Ficon.jpeg?alt=media"

    fun getStorageUrl(path: String): String {
        // Local resources: octopus.png, protein_planner_banner.avif exist in webApp resources
        if (path.startsWith("images/")) {
            val local = "/images/" + path.removePrefix("images/")
            // Known local assets — use relative path when deployed
            if (path == "images/octopus.png" || path == "images/protein_planner_banner.avif") return local
            // Other images: use Firebase Storage (may 404 if not uploaded)
            val bucket = "borshi-6fafe.firebasestorage.app"
            val encodedPath = js("encodeURIComponent")(path.replace(" ", "%20")) as String
            return "https://firebasestorage.googleapis.com/v0/b/$bucket/o/$encodedPath?alt=media"
        }
        val bucket = "borshi-6fafe.firebasestorage.app"
        val encodedPath = js("encodeURIComponent")(path.replace(" ", "%20")) as String
        return "https://firebasestorage.googleapis.com/v0/b/$bucket/o/$encodedPath?alt=media"
    }

    fun getProductImageUrl(productId: String): String {
        // Use the correct Firebase Storage bucket and path format
        // Note: %2F is the URL-encoded forward slash for Firebase Storage paths
        val bucket = "borshi-6fafe.firebasestorage.app"
        // Don't encode the productId since it's already a valid identifier (e.g., prd_aar_curry_cut)
        return "https://firebasestorage.googleapis.com/v0/b/$bucket/o/prd_image%2F$productId.avif?alt=media"
    }

    /**
     * Lists all images in a Firebase Storage folder (e.g. sliding_banner/webapp/window1)
     * and returns their download URLs. Used for dynamic hero banners.
     */
    suspend fun getSlidingBannerUrls(folderPath: String): List<String> {
        return try {
            init()
            val listRef = FirebaseStorage.ref(storage, folderPath)
            val result = FirebaseStorage.listAll(listRef).await()
            val items = result.items as? Array<dynamic> ?: return emptyList()
            items.mapNotNull { item ->
                try {
                    FirebaseStorage.getDownloadURL(item).await() as? String
                } catch (_: Throwable) { null }
            }
        } catch (e: Throwable) {
            if (e.message?.contains("left the composition") != true) {
                console.error("getSlidingBannerUrls failed for $folderPath:", e)
            }
            emptyList()
        }
    }

    /**
     * Dynamically lists all images in the feedback/ folder and returns their download URLs.
     * Adding/removing images in Firebase Storage is all that's needed — no code change required.
     */
    suspend fun getFeedbackImageUrls(): List<String> {
        return try {
            init()
            val listRef = FirebaseStorage.ref(storage, "feedback")
            val result = FirebaseStorage.listAll(listRef).await()
            val items = result.items as? Array<dynamic> ?: return emptyList()
            items.mapNotNull { item ->
                try {
                    FirebaseStorage.getDownloadURL(item).await() as? String
                } catch (_: Throwable) { null }
            }
        } catch (e: Throwable) {
            console.error("getFeedbackImageUrls failed:", e)
            emptyList()
        }
    }

    /** Plan card images from page_images/plans/plan_images/ (balanced_wellness, lean, muscle_building, starter, vitality) */
    fun getPlanImageUrl(category: PlanCategory): String {
        val bucket = "borshi-6fafe.firebasestorage.app"
        val filename = when (category) {
            PlanCategory.MUSCLE_BUILDING, PlanCategory.ATHLETE -> "muscle_building"
            PlanCategory.WEIGHT_LOSS, PlanCategory.DIABETES -> "lean"
            PlanCategory.GENERAL_HEALTH -> "balanced_wellness"
            PlanCategory.PREGNANCY, PlanCategory.SENIOR, PlanCategory.HEART_HEALTH -> "vitality"
        } + ".avif"
        val path = "page_images/plans/plan_images/$filename"
        val encodedPath = js("encodeURIComponent")(path) as String
        return "https://firebasestorage.googleapis.com/v0/b/$bucket/o/$encodedPath?alt=media"
    }

    fun init() {
        if (initialized) return
        if (!FirebaseConfig.isConfigured()) {
            throw IllegalStateException("FirebaseConfig is not set. Please update FirebaseConfig.kt")
        }
        val apps = FirebaseApp.getApps()
        if (apps.isNotEmpty()) {
            app = apps[0]
        } else {
            val config = js("({})")
            config.apiKey = FirebaseConfig.apiKey
            config.authDomain = FirebaseConfig.authDomain
            config.projectId = FirebaseConfig.projectId
            config.storageBucket = FirebaseConfig.storageBucket
            config.messagingSenderId = FirebaseConfig.messagingSenderId
            config.appId = FirebaseConfig.appId
            app = FirebaseApp.initializeApp(config)
        }
        // Initialize Firestore with memory-only cache to avoid IndexedDB errors
        // (IndexedDB can fail in certain browser states, corrupted storage, or quota issues)
        try {
            val firestoreModule = js("require('firebase/firestore')")
            db = firestoreModule.initializeFirestore(app, js("({localCache: require('firebase/firestore').memoryLocalCache()})"))
        } catch (e: Throwable) {
            console.warn("initializeFirestore with memoryLocalCache failed, falling back to getFirestore:", e)
            db = FirebaseFirestore.getFirestore(app)
        }
        val host = kotlinx.browser.window.location.hostname
        if (host == "localhost" || host == "127.0.0.1") {
            js("(require('firebase/firestore').useFirestoreEmulator || require('firebase/firestore').connectFirestoreEmulator)(db, '127.0.0.1', 8085)")
            console.log("Firebase: using Firestore emulator at 127.0.0.1:8085")
        }
        auth = FirebaseAuth.getAuth(app)
        functions = FirebaseFunctions.getFunctions(app, "us-central1")
        storage = FirebaseStorage.getStorage(app)
        initialized = true

        // Expose Firebase instances on window so cashfree-service.js can use the
        // modular SDK without the compat firebase.auth() / firebase.functions() APIs.
        // Use kotlinx.browser.window.asDynamic() so the Kotlin fields are captured
        // correctly — js("window.x = auth") does NOT work because bare identifiers
        // inside js() strings are raw JS names, not Kotlin field references.
        kotlinx.browser.window.asDynamic().__borshiAuth = auth
        kotlinx.browser.window.asDynamic().__borshiFunctions = functions

        // Expose httpsCallable from the webpack bundle so cashfree-service.js can
        // call Cloud Functions without a dynamic import().
        // We use a lambda to bridge the call to the external object method which is already loaded.
        kotlinx.browser.window.asDynamic().__borshiHttpsCallable = { functionsInstance: dynamic, name: String ->
            FirebaseFunctions.httpsCallable(functionsInstance, name)
        }

        console.log("Firebase init complete. Window check:",
            "auth=", kotlinx.browser.window.asDynamic().__borshiAuth != null,
            "functions=", kotlinx.browser.window.asDynamic().__borshiFunctions != null,
            "httpsCallable=", kotlinx.browser.window.asDynamic().__borshiHttpsCallable != null
        )
    }

    suspend fun signIn(email: String, password: String): dynamic {
        init()
        val result = FirebaseAuth.signInWithEmailAndPassword(auth, email, password).await()
        ensureUserDocument(result.user.uid as String, email)
        return result
    }

    suspend fun signUp(
        email: String,
        password: String,
        name: String = "",
        phone: String = "",
        addressLine1: String = "",
        addressLine2: String = "",
        city: String = "",
        state: String = "",
        postalCode: String = ""
    ): dynamic {
        init()
        val result = FirebaseAuth.createUserWithEmailAndPassword(auth, email, password).await()
        ensureUserDocument(
            uid = result.user.uid as String,
            email = email,
            name = name,
            phone = phone,
            addressLine1 = addressLine1,
            addressLine2 = addressLine2,
            city = city,
            state = state,
            postalCode = postalCode
        )
        return result
    }

    private suspend fun ensureUserDocument(
        uid: String,
        email: String? = null,
        name: String? = null,
        phone: String? = null,
        addressLine1: String? = null,
        addressLine2: String? = null,
        city: String? = null,
        state: String? = null,
        postalCode: String? = null
    ) {
        try {
            val userRef = FirebaseFirestore.doc(db, "users", uid)
            val userSnap = FirebaseFirestore.getDoc(userRef).await()

            if (!userSnap.exists()) {
                console.log("Creating user document for:", uid)
                val userData = js("({})")
                userData.name = name ?: ""
                userData.email = email ?: ""
                userData.phone = phone ?: ""
                userData.createdAt = Date.now()

                val defaultAddress = js("({})")
                defaultAddress.id = "addr_${Date.now().toLong()}"
                defaultAddress.name = name ?: ""
                defaultAddress.phone = phone ?: ""
                defaultAddress.email = email ?: ""
                defaultAddress.line1 = addressLine1 ?: ""
                defaultAddress.line2 = addressLine2 ?: ""
                defaultAddress.city = city ?: ""
                defaultAddress.state = state ?: ""
                defaultAddress.postalCode = postalCode ?: ""
                defaultAddress.isDefault = true
                userData.addresses = js("[defaultAddress]")
                userData.lastUsedAddressId = defaultAddress.id

                FirebaseFirestore.setDoc(userRef, userData).await()
                console.log("User document created with default address")
            } else if (name != null && name.isNotBlank()) {
                val existing = userSnap.data()
                val updates = js("({})")
                var needsUpdate = false
                if ((existing.name as? String).isNullOrBlank()) {
                    updates.name = name; needsUpdate = true
                }
                if ((existing.phone as? String).isNullOrBlank() && !phone.isNullOrBlank()) {
                    updates.phone = phone; needsUpdate = true
                }
                if ((existing.email as? String).isNullOrBlank() && !email.isNullOrBlank()) {
                    updates.email = email; needsUpdate = true
                }
                if (needsUpdate) {
                    FirebaseFirestore.updateDoc(userRef, updates).await()
                }
            }
        } catch (e: Throwable) {
            console.error("Error ensuring user document:", e)
        }

        try {
            val callable = FirebaseFunctions.httpsCallable(functions, "ensureUserProvisioned")
            callable(js("({})")).await()
        } catch (e: Throwable) {
            console.warn("ensureUserProvisioned failed (non-fatal):", e)
        }
    }

    /**
     * Updates the current user's profile with required sign-up fields (name, phone, address).
     * Used for Google/Anonymous users who need to complete their profile before accessing the app.
     */
    suspend fun updateUserProfileRequiredFields(
        name: String,
        phone: String,
        addressLine1: String,
        addressLine2: String = "",
        city: String,
        state: String = "",
        postalCode: String
    ) {
        val uid = requireUid()
        val userRef = FirebaseFirestore.doc(db, "users", uid)
        val userSnap = FirebaseFirestore.getDoc(userRef).await()
        val userData = if (userSnap.exists()) userSnap.data() else js("({})")

        val addrArray = userData.addresses
        val currentAddresses = (if (addrArray != null) (addrArray as Array<*>).toMutableList() else mutableListOf<Any?>()) as MutableList<Any?>
        val defaultAddr = currentAddresses.firstOrNull { addr ->
            (addr.asDynamic().isDefault == true)
        }
        val defaultId = "addr_${Date.now().toLong()}"
        val email = auth?.currentUser?.email as? String ?: ""

        val defaultAddress = js("({})")
        defaultAddress.id = if (defaultAddr != null) (defaultAddr.asDynamic().id as? String) ?: defaultId else defaultId
        defaultAddress.name = name
        defaultAddress.phone = phone
        defaultAddress.email = email
        defaultAddress.line1 = addressLine1
        defaultAddress.line2 = addressLine2
        defaultAddress.city = city
        defaultAddress.state = state
        defaultAddress.postalCode = postalCode
        defaultAddress.isDefault = true

        val newAddresses = if (defaultAddr != null) {
            currentAddresses.map { addr ->
                if (addr.asDynamic().isDefault == true) defaultAddress else addr
            }.toMutableList()
        } else {
            currentAddresses.forEach { it.asDynamic().isDefault = false }
            (mutableListOf<Any?>().apply { addAll(currentAddresses); add(0, defaultAddress) }) as MutableList<Any?>
        }

        val updates = js("({})")
        updates.name = name
        updates.phone = phone
        updates.addresses = newAddresses.toTypedArray()
        updates.lastUsedAddressId = defaultAddress.id

        if (userSnap.exists()) {
            FirebaseFirestore.updateDoc(userRef, updates).await()
        } else {
            updates.createdAt = Date.now()
            updates.email = email
            FirebaseFirestore.setDoc(userRef, updates).await()
        }
    }

    private fun resolveRecaptchaCtor(): dynamic {
        return try {
            val direct = js("typeof RecaptchaVerifier !== 'undefined' ? RecaptchaVerifier : undefined")
            if (direct != undefined) return direct
            val mod = js("require('firebase/auth')")
            mod?.RecaptchaVerifier ?: mod?.default?.RecaptchaVerifier ?: mod?.auth?.RecaptchaVerifier
        } catch (_: Throwable) {
            null
        }
    }

    private fun buildRecaptchaVerifier(auth: dynamic, containerId: String, params: dynamic): dynamic {
        @Suppress("UNUSED_VARIABLE")
        val ctor = resolveRecaptchaCtor()
            ?: throw IllegalStateException("RecaptchaVerifier unavailable; ensure firebase/auth modular SDK is bundled")
        return try {
            js("new ctor(auth, containerId, params)")
        } catch (_: Throwable) {
            js("new ctor(containerId, params, auth)")
        }
    }

    suspend fun sendOtp(phoneNumber: String, recaptchaContainerId: String) {
        init()
        val container = document.getElementById(recaptchaContainerId)
            ?: throw IllegalStateException("Missing reCAPTCHA container #$recaptchaContainerId on the page")
        resetRecaptchaVerifier()
        // Clear container so a fresh RecaptchaVerifier can be rendered (prevents "already been rendered" on retry)
        container.innerHTML = ""
        val recaptchaParams = js("({})")
        recaptchaParams.size = "invisible"
        recaptchaParams.callback = { console.info("reCAPTCHA solved") }

        val verifier = buildRecaptchaVerifier(auth, recaptchaContainerId, recaptchaParams)
        try {
            (verifier.render() as Promise<dynamic>).await()
        } catch (_: Throwable) {
            // if render is not a function, ignore; signInWithPhoneNumber will throw if verifier is invalid
        }
        recaptchaVerifier = verifier

        confirmationResult = FirebaseAuth
            .signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier!!)
            .await()
    }

    suspend fun verifyOtp(otp: String): dynamic {
        val trimmedOtp = otp.trim()
        require(trimmedOtp.length >= 6) { "Enter the 6-digit OTP sent to your device" }
        val pendingConfirmation = confirmationResult
            ?: throw IllegalStateException("Please request an OTP before verifying")

        // Explicitly cast to Promise for await()
        val promise = pendingConfirmation.confirm(trimmedOtp) as Promise<dynamic>
        val result = promise.await().also {
            confirmationResult = null
            resetRecaptchaVerifier()
        }

        // Ensure user document exists after phone authentication
        val uid = result.user?.uid as? String
        if (uid != null) {
            val phoneNumber = result.user?.phoneNumber as? String
            ensureUserDocument(uid, phoneNumber)
        }

        return result
    }

    suspend fun signOut() {
        init()
        FirebaseAuth.signOut(auth).await()
    }

    suspend fun signInWithGoogle(): dynamic {
        init()
        val authMod = js("require('firebase/auth')")
        val GoogleAuthProvider = authMod.GoogleAuthProvider
        val provider = js("new GoogleAuthProvider()")
        val signInWithPopup = authMod.signInWithPopup
        val result = (signInWithPopup(auth, provider) as Promise<dynamic>).await()
        val user = result.user
        if (user != null) {
            val uid = user.uid as? String
            val email = user.email as? String
            val name = user.displayName as? String
            if (uid != null) ensureUserDocument(uid, email, name, null)
        }
        return result
    }

    suspend fun signInAnonymously(): dynamic {
        init()
        val authMod = js("require('firebase/auth')")
        val signInAnonymouslyFn = authMod.signInAnonymously
        val result = (signInAnonymouslyFn(auth) as Promise<dynamic>).await()
        val uid = result.user?.uid as? String
        if (uid != null) ensureUserDocument(uid, null, null, null)
        return result
    }

    suspend fun getCategories(): List<Category> {
        init()
        try {
            val col = FirebaseFirestore.collection(db, "categories")
            val snapshot = FirebaseFirestore.getDocs(col).await()
            val docs = snapshot.docs as Array<dynamic>
            return docs.map { doc ->
                val data = doc.data()

                // Use doc.id (or data.id) as the technical key (e.g., "cat_fish_freshwater")
                // This key is used mapping to the image file.
                val docId = doc.id as? String ?: ""
                val dataId = data.id as? String
                val catKey = if (!dataId.isNullOrBlank()) dataId else docId

                val nameField = data.name as? String ?: ""
                val displayNameField = data.displayName as? String

                // Determine the best display name
                val finalName = if (!displayNameField.isNullOrBlank()) {
                    displayNameField
                } else {
                    // If name field exists and isn't technical, use it. Otherwise derive from key.
                    val source = if (nameField.isNotBlank() && !nameField.startsWith("cat_")) nameField else catKey

                    if (source.startsWith("cat_")) {
                        source.replace("cat_", "")
                            .replace("meat_", "")
                            .replace("fish_", "")
                            .replace("seafood_", "")
                            .replace("_", " ")
                            .split(" ").joinToString(" ") { it.replaceFirstChar { c -> c.uppercase() } }
                    } else {
                        source
                    }
                }

                Category(
                    id = catKey,
                    name = finalName,
                    description = data.description as? String ?: "",
                    // Pass the technical key to match exact image filenames (e.g. cat_fish_freshwater.avif)
                    imageUrl = getCategoryImageUrl(catKey, finalName),
                    emoji = data.emoji as? String ?: "🐟"
                )
            }.sortedBy { it.name }
        } catch (e: Throwable) {
            console.error("Error fetching categories:", e)
            return emptyList()
        }
    }

    suspend fun getProducts(): List<Product> {
        init()

        // First fetch categories to build the mapping (use displayName for UI)
        val categoriesCol = FirebaseFirestore.collection(db, "categories")
        val categoriesSnapshot = FirebaseFirestore.getDocs(categoriesCol).await()
        val categoriesDocs = categoriesSnapshot.docs as Array<dynamic>
        val categoryIdToName = categoriesDocs.associate { doc ->
            val data = doc.data()
            // Fix: Use doc.id if data.id is missing or empty (which it is for existing categories)
            val catId = (data.id as? String)?.takeIf { it.isNotEmpty() } ?: (doc.id as String)
            // Prefer displayName for UI consistency
            val displayName = data.displayName as? String ?: data.name as? String ?: "Unknown"
            catId to displayName
        }

        // Then fetch products and resolve category names
        val col = FirebaseFirestore.collection(db, "products")
        val snapshot = FirebaseFirestore.getDocs(col).await()
        val docs = snapshot.docs as Array<dynamic>
        return docs.map { doc ->
            val data = doc.data()
            val docId = doc.id as? String ?: ""  // Get document ID
            val image = getProductImageUrl(docId)
            val categoryId = data.categoryId as? String ?: ""
            // Map categoryId to display name from categories collection
            val categoryName = categoryIdToName[categoryId] ?: categoryId.ifEmpty { "All" }

            // Parse tags
            val tagsData = data.tags
            val tags = if (tagsData != null) {
                ProductTags(
                    waterType = parseStringArray(tagsData.waterType),
                    boneType = parseStringArray(tagsData.boneType),
                    benefit = parseStringArray(tagsData.benefit),
                    cutType = parseStringArray(tagsData.cutType),
                    omega3 = parseStringArray(tagsData.omega3),
                    minerals = parseStringArray(tagsData.minerals),
                    vitamins = parseStringArray(tagsData.vitamins),
                    cookStyle = parseStringArray(tagsData.cookStyle),
                    diet = parseStringArray(tagsData.diet),
                    spiceFit = parseStringArray(tagsData.spiceFit),
                    handling = parseStringArray(tagsData.handling),
                    allergen = parseStringArray(tagsData.allergen)
                )
            } else {
                ProductTags()
            }

            Product(
                id = docId,  // Include document ID
                name = data.name as? String ?: "",
                price = (data.price as? Number)?.toDouble() ?: 0.0,
                imageUrl = image,
                category = categoryName,  // Now using the displayName from categories
                tags = tags,
                productInfo = parseProductInfo(data)
            )
        }
    }

    private fun parseStringArray(data: dynamic): List<String> {
        return try {
            val array = data as? Array<dynamic>
            array?.mapNotNull { it as? String } ?: emptyList()
        } catch (e: Throwable) {
            emptyList()
        }
    }

    private fun parseProductInfo(data: dynamic): ProductInfo {
        return try {
            val info = data?.productInfo
            if (info == null) return ProductInfo()
            ProductInfo(
                culinaryProfile = info.culinaryProfile as? String ?: "",
                cookingTips = parseStringArray(info.cookingTips),
                healthBenefits = parseStringArray(info.healthBenefits),
                cons = parseStringArray(info.cons),
                allergens = parseStringArray(info.allergens),
                storageInstructions = info.storageInstructions as? String ?: "",
                certifications = parseStringArray(info.certifications)
            )
        } catch (_: Throwable) {
            ProductInfo()
        }
    }

    suspend fun getPublicPlans(): List<NutritionistBasePlan> {
        init()
        try {
            val col = FirebaseFirestore.collection(db, "nutritionist_base_plans")
            val q = FirebaseFirestore.query(
                col,
                FirebaseFirestore.where("isActive", "==", true),
                FirebaseFirestore.where("isPublic", "==", true)
            )
            val snapshot = FirebaseFirestore.getDocs(q).await()
            val docs = snapshot.docs as Array<dynamic>
            return docs.map { doc ->
                val data = doc.data()
                NutritionistBasePlan(
                    basePlanId = data.basePlanId as? String ?: doc.id as? String ?: "",
                    planName = data.planName as? String ?: "",
                    category = PlanCategory.valueOf((data.category as? String ?: "GENERAL_HEALTH").uppercase()),
                    targetAudience = data.targetAudience as? String ?: "",
                    description = data.description as? String ?: "",
                    nutritionalTargets = NutritionalTargets(
                        proteinPerWeek = (data.nutritionalTargets?.proteinPerWeek as? Number)?.toInt() ?: 0,
                        caloriesPerDay = (data.nutritionalTargets?.caloriesPerDay as? Number)?.toInt() ?: 0,
                        omega3PerWeek = (data.nutritionalTargets?.omega3PerWeek as? Number)?.toInt() ?: 0
                    ),
                    imageUrl = data.imageUrl as? String ?: "",
                    badgeText = data.badgeText as? String ?: "",
                    badgeColor = data.badgeColor as? String ?: "",
                    pricingTemplate = PricingTemplate(
                        weeklyBase = (data.pricingTemplate?.weeklyBase as? Number)?.toDouble() ?: 0.0
                    )
                )
            }
        } catch (e: Throwable) {
            if (e.message?.contains("left the composition") != true) {
                console.error("Error fetching public plans:", e)
            }
            return emptyList()
        }
    }

    suspend fun getNutritionists(): List<NutritionistProfile> {
        init()
        try {
            val col = FirebaseFirestore.collection(db, "nutritionist_profiles")
            val snapshot = FirebaseFirestore.getDocs(col).await()
            val docs = snapshot.docs as Array<dynamic>
            return docs.map { doc ->
                val data = doc.data()
                NutritionistProfile(
                    nutritionistId = data.nutritionistId as? String ?: doc.id as? String ?: "",
                    name = data.name as? String ?: "",
                    specialization = data.specialization as? String ?: "",
                    qualifications = data.qualifications as? String ?: "",
                    bio = data.bio as? String ?: "",
                    profileImage = data.profileImage as? String ?: "",
                    experienceYears = (data.experienceYears as? Number)?.toInt() ?: 0,
                    rating = (data.rating as? Number)?.toDouble() ?: 0.0,
                    totalClients = (data.totalClients as? Number)?.toInt() ?: 0
                )
            }
        } catch (e: Throwable) {
            if (e.message?.contains("left the composition") != true) {
                console.error("Error fetching nutritionists:", e)
            }
            return emptyList()
        }
    }

    suspend fun getProteinPlans(): List<ProteinPlan> {
        init()
        val allProducts = getProducts()
        val col = FirebaseFirestore.collection(db, "protein_plans")
        val snapshot = FirebaseFirestore.getDocs(col).await()
        val docs = snapshot.docs as Array<dynamic>
        return docs.map { doc ->
            val data = doc.data()
            val mixItemsArray = data.mix_items as? Array<dynamic> ?: emptyArray()
            val mixItems = mixItemsArray.mapNotNull { item ->
                val productId = item.product_id as? String ?: item.item as? String
                val qty = item.qty as? String ?: ""
                if (productId != null) {
                    val product = allProducts.find {
                        it.id == productId || it.name.equals(productId, ignoreCase = true)
                    }
                    com.example.borshi.web.model.PlanMixItem(
                        productId = productId,
                        qty = qty,
                        productName = product?.name ?: productId,
                        productImage = getProductImageUrl(productId),
                        productPrice = product?.price ?: 0.0
                    )
                } else null
            }
            ProteinPlan(
                planId = data.id as? String ?: "",
                planName = data.plan_name as? String ?: "",
                description = data.description as? String ?: "",
                duration = data.duration as? String ?: "Weekly",
                deliveriesPerWeek = (data.deliveries_per_week as? Number)?.toInt() ?: 1,
                mixItems = mixItems,
                consultationEligible = data.consultation_eligible as? Boolean ?: false,
                price = (data.price as? Number)?.toDouble() ?: 0.0,
                imageUrl = data.image_url as? String ?: ""
            )
        }
    }

    suspend fun submitOrder(request: OrderRequest): String {
        val uid = requireUid()
        val ordersCol = FirebaseFirestore.collection(db, "orders")
        val data = js("({})")
        data.userId = uid
        data.customerName = request.customerName
        data.customerPhone = request.customerPhone
        data.paymentMethod = request.paymentMethod
        data.status = "pending"
        data.totalAmount = request.totals.oneTimeSubtotal + request.totals.subscriptionFirstDeliverySubtotal + request.totals.deliveryFee + request.totals.taxAndPackaging
        data.payTodayAmount = request.totals.payTodayTotal
        data.deliveryAddress = "${request.address.line1}, ${request.address.city} - ${request.address.postalCode}"
        data.createdAt = Date.now()
        val coordPair = request.address.coordinates
        if (coordPair != null) {
            val coords = js("({})")
            coords.latitude = coordPair.first
            coords.longitude = coordPair.second
            data.coordinates = coords
        }
        val itemsArray = js("[]")
        request.itemsOneTime.forEach { item ->
            val entry = js("({})")
            entry.productId = item.productId
            entry.name = item.name
            entry.quantity = item.quantity
            entry.price = item.price
            entry.type = OrderItemType.ONE_TIME.name
            entry.weight = item.weight
            entry.imageUrl = item.imageUrl
            itemsArray.push(entry)
        }
        request.itemsSubscription.forEach { item ->
            val entry = js("({})")
            entry.productId = item.productId
            entry.name = item.name
            entry.quantity = item.quantityPerDelivery
            entry.price = item.price
            entry.type = OrderItemType.SUBSCRIPTION.name
            entry.packSize = item.packSize
            entry.scheduleSummary = item.schedule.toSummaryString()
            entry.imageUrl = item.imageUrl
            itemsArray.push(entry)
        }
        data.items = itemsArray
        val docRef = FirebaseFirestore.addDoc(ordersCol, data).await()
        return docRef.id as? String ?: ""
    }

    suspend fun getOrder(orderId: String): WebOrder? {
        init()
        return try {
            val docRef = FirebaseFirestore.doc(db, "orders", orderId)
            val snapshot = FirebaseFirestore.getDoc(docRef).await()
            if (!snapshot.exists()) return null
            // Re-use getOrders() parsing by fetching user orders and finding by id
            getOrders().firstOrNull { it.id == orderId }
        } catch (e: Throwable) {
            console.error("Failed to fetch order $orderId", e)
            null
        }
    }

    suspend fun verifyPayment(orderId: String): String {
        init()
        return try {
            val callable = FirebaseFunctions.httpsCallable(functions, "verifyPayment")
            val payload = js("({})")
            payload.orderId = orderId
            val result = callable(payload).await()
            result.data?.paymentStatus as? String ?: "UNKNOWN"
        } catch (e: Throwable) {
            console.error("Payment verification failed for $orderId:", e)
            throw e
        }
    }

    /**
     * Fallback: credit wallet for top-up orders when webhook did not run.
     * Call after payment is verified PAID so balance reflects.
     */
    suspend fun completeWalletTopUpIfPaid(orderId: String) {
        init()
        try {
            val callable = FirebaseFunctions.httpsCallable(functions, "completeWalletTopUpIfPaid")
            val payload = js("({})")
            payload.orderId = orderId
            callable(payload).await()
        } catch (e: Throwable) {
            console.warn("completeWalletTopUpIfPaid failed (may not be a top-up order):", e)
        }
    }

    private fun resetRecaptchaVerifier() {
        @Suppress("UNUSED_VARIABLE")
        val v = recaptchaVerifier ?: return
        try {
            @Suppress("UNUSED_VARIABLE")
            val clear = js("v && v.clear ? v.clear : null")
            if (clear != null) js("clear.call(v)")
        } catch (error: Throwable) {
            console.warn("Failed to reset Firebase reCAPTCHA", error)
        } finally {
            recaptchaVerifier = null
        }
    }

    suspend fun debugCallable() {
        console.log("Debug: Starting callable test...")
        val payload = js("({})")
        val amount = js("({})")
        amount.currency = "INR"
        amount.minorUnits = 1000 // ₹10.00

        payload.amount = amount
        payload.idempotencyKey = "DEBUG_" + Date.now().toString()

        try {
            val result = callFunction("createTopUpIntent", payload)
            console.log("Debug: createTopUpIntent success:", result)
        } catch (e: Throwable) {
            console.error("Debug: createTopUpIntent failed:", e)
        }
    }
}

data class WalletTransaction(
    val id: String,
    val amount: Double,
    val type: String,
    val description: String,
    val timestampMillis: Long = 0L
)

data class DailyDelivery(
    val id: String,
    val productName: String,
    val quantity: Int,
    val date: String,
    val status: String
)

data class Vacation(
    val id: String,
    val startDate: Date,
    val endDate: Date,
    val label: String
)

data class PlanMixItem(
    val productId: String,
    val qty: String,
    val productName: String,
    val productImage: String,
    val productPrice: Double
)

data class SavedAddress(
    val id: String = "",
    val name: String = "",
    val phone: String = "",
    val email: String = "",
    val line1: String = "",
    val line2: String = "",
    val city: String = "",
    val state: String = "",
    val postalCode: String = "",
    val isDefault: Boolean = false
)
